import React, {
  useMemo,
  useState,
  type ChangeEvent,
  type DragEvent,
  type FormEvent,
} from 'react';

import {
  CalendarDays,
  ImagePlus,
  Loader2,
  MapPin,
  Save,
  Ticket,
  Trophy,
  Upload,
  Wallet,
  X,
} from 'lucide-react';

import axios from 'axios';

import {
  getPosterPresignUrl,
} from '../../poster/services/posterService';

import {
  createTournament,
} from '../services/tournament.service';

import type {
  TournamentCreateRequest,
} from '../types/tournament.types';


type TournamentCreateFormValue = {
  tournamentTitle: string;

  tournamentPoster: string;

  posterFile: File | null;

  tournamentEntry: string;

  tournamentField: string;

  date: string;

  startTime: string;

  tournamentPrice: string;
};


type FormErrors = Partial<
  Record<
    keyof TournamentCreateFormValue,
    string
  >
>;


type ApiErrorResponse = {
  message?: string;
  code?: string;
};


type TournamentCreateFormProps = {
  onCreated: (
    month: string,
  ) => void;
};


const initialForm:
  TournamentCreateFormValue = {
    tournamentTitle: '',
    tournamentPoster: '',
    posterFile: null,

    tournamentEntry: '',

    tournamentField: '',

    date: '',

    startTime: '',

    tournamentPrice: '',
  };


const MAX_POSTER_SIZE =
  5 * 1024 * 1024;


const ACCEPTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
];


const fileToDataUrl = (
  file: File,
): Promise<string> =>
  new Promise(
    (
      resolve,
      reject,
    ) => {
      const reader =
        new FileReader();

      reader.onload = () => {
        if (
          typeof reader.result ===
          'string'
        ) {
          resolve(
            reader.result,
          );

          return;
        }

        reject(
          new Error(
            '이미지 URL 변환에 실패했습니다.',
          ),
        );
      };

      reader.onerror = () => {
        reject(
          new Error(
            '이미지 파일을 읽을 수 없습니다.',
          ),
        );
      };

      reader.readAsDataURL(
        file,
      );
    },
  );


const getErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  if (
    axios.isAxiosError<
      ApiErrorResponse
    >(error)
  ) {
    return (
      error.response?.data
        ?.message ??
      error.message ??
      fallback
    );
  }

  if (
    error instanceof Error
  ) {
    return error.message;
  }

  return fallback;
};


const TournamentCreateForm:
  React.FC<
    TournamentCreateFormProps
  > = ({
    onCreated,
  }) => {
    const [
      form,
      setForm,
    ] =
      useState<TournamentCreateFormValue>(
        initialForm,
      );

    const [
      errors,
      setErrors,
    ] =
      useState<FormErrors>(
        {},
      );

    const [
      isSubmitting,
      setIsSubmitting,
    ] =
      useState(false);

    const [
      isDragging,
      setIsDragging,
    ] =
      useState(false);


    const formattedSchedule =
      useMemo(
        () => {
          if (
            !form.date ||
            !form.startTime
          ) {
            return '날짜와 시작 시각을 입력해주세요.';
          }

          const date =
            new Date(
              `${form.date}T${form.startTime}:00`,
            );

          if (
            Number.isNaN(
              date.getTime(),
            )
          ) {
            return '유효한 일시를 입력해주세요.';
          }

          return date.toLocaleString(
            'ko-KR',
            {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              weekday: 'short',
              hour: '2-digit',
              minute: '2-digit',
            },
          );
        },
        [
          form.date,
          form.startTime,
        ],
      );


    const formattedSeedCount =
      useMemo(
        () => {
          if (
            !form.tournamentPrice
          ) {
            return '-';
          }

          return `${Number(
            form.tournamentPrice,
          ).toLocaleString(
            'ko-KR',
          )}개`;
        },
        [
          form.tournamentPrice,
        ],
      );


    const clearFieldError = (
      name:
        keyof TournamentCreateFormValue,
    ): void => {
      setErrors(
        (previous) => {
          if (
            !previous[name]
          ) {
            return previous;
          }

          const next = {
            ...previous,
          };

          delete next[name];

          return next;
        },
      );
    };


    const handleInputChange = (
      event:
        ChangeEvent<HTMLInputElement>,
    ): void => {
      const {
        name,
        value,
      } =
        event.target;

      const fieldName =
        name as keyof TournamentCreateFormValue;

      setForm(
        (previous) => ({
          ...previous,

          [fieldName]:
            value,
        }),
      );

      clearFieldError(
        fieldName,
      );
    };


    const applyPosterFile =
      async (
        file: File,
      ): Promise<void> => {
        if (
          !ACCEPTED_IMAGE_TYPES.includes(
            file.type,
          )
        ) {
          setErrors(
            (previous) => ({
              ...previous,

              posterFile:
                'PNG 또는 JPEG 이미지만 등록할 수 있습니다.',
            }),
          );

          return;
        }

        if (
          file.size >
          MAX_POSTER_SIZE
        ) {
          setErrors(
            (previous) => ({
              ...previous,

              posterFile:
                '포스터 이미지는 5MB 이하만 등록할 수 있습니다.',
            }),
          );

          return;
        }

        try {
          const dataUrl =
            await fileToDataUrl(
              file,
            );

          setForm(
            (previous) => ({
              ...previous,

              posterFile:
                file,

              tournamentPoster:
                dataUrl,
            }),
          );

          clearFieldError(
            'posterFile',
          );

          clearFieldError(
            'tournamentPoster',
          );
        } catch (
          error
        ) {
          console.error(
            '포스터 이미지 처리 실패:',
            error,
          );

          setErrors(
            (previous) => ({
              ...previous,

              posterFile:
                '이미지 변환에 실패했습니다.',
            }),
          );
        }
      };


    const handlePosterChange =
      async (
        event:
          ChangeEvent<HTMLInputElement>,
      ): Promise<void> => {
        const file =
          event.target
            .files?.[0];

        if (file) {
          await applyPosterFile(
            file,
          );
        }

        event.target.value =
          '';
      };


    const handleDragEnter = (
      event:
        DragEvent<HTMLLabelElement>,
    ): void => {
      event.preventDefault();
      event.stopPropagation();

      setIsDragging(
        true,
      );
    };


    const handleDragOver = (
      event:
        DragEvent<HTMLLabelElement>,
    ): void => {
      event.preventDefault();
      event.stopPropagation();

      event.dataTransfer.dropEffect =
        'copy';

      setIsDragging(
        true,
      );
    };


    const handleDragLeave = (
      event:
        DragEvent<HTMLLabelElement>,
    ): void => {
      event.preventDefault();
      event.stopPropagation();

      if (
        event.currentTarget.contains(
          event.relatedTarget as
            | Node
            | null,
        )
      ) {
        return;
      }

      setIsDragging(
        false,
      );
    };


    const handleDrop =
      async (
        event:
          DragEvent<HTMLLabelElement>,
      ): Promise<void> => {
        event.preventDefault();
        event.stopPropagation();

        setIsDragging(
          false,
        );

        const file =
          event.dataTransfer
            .files?.[0];

        if (file) {
          await applyPosterFile(
            file,
          );
        }
      };


    const handleRemovePoster =
      (): void => {
        setForm(
          (previous) => ({
            ...previous,

            posterFile:
              null,

            tournamentPoster:
              '',
          }),
        );

        clearFieldError(
          'posterFile',
        );

        clearFieldError(
          'tournamentPoster',
        );
      };


    const validateForm =
      (): boolean => {
        const nextErrors:
          FormErrors = {};

        if (
          !form.tournamentTitle.trim()
        ) {
          nextErrors.tournamentTitle =
            '대회 이름을 입력해주세요.';
        }


        if (
          !form.posterFile
        ) {
          nextErrors.posterFile =
            '대회 포스터 이미지를 선택해주세요.';
        }


        const tournamentEntry =
          Number(
            form.tournamentEntry,
          );

        if (
          !form.tournamentEntry
        ) {
          nextErrors.tournamentEntry =
            'MAX 엔트리를 입력해주세요.';
        } else if (
          !Number.isInteger(
            tournamentEntry,
          ) ||
          tournamentEntry <
            1
        ) {
          nextErrors.tournamentEntry =
            'MAX 엔트리는 1 이상의 정수여야 합니다.';
        }


        if (
          !form.tournamentField.trim()
        ) {
          nextErrors.tournamentField =
            '대회 장소를 입력해주세요.';
        }


        if (
          !form.date
        ) {
          nextErrors.date =
            '대회 날짜를 선택해주세요.';
        }


        if (
          !form.startTime
        ) {
          nextErrors.startTime =
            '대회 시작 시각을 선택해주세요.';
        }


        const tournamentPrice =
          Number(
            form.tournamentPrice,
          );

        if (
          !form.tournamentPrice
        ) {
          nextErrors.tournamentPrice =
            '필요 시드 개수를 입력해주세요.';
        } else if (
          !Number.isInteger(
            tournamentPrice,
          ) ||
          tournamentPrice <
            0
        ) {
          nextErrors.tournamentPrice =
            '필요 시드 개수는 0 이상의 정수여야 합니다.';
        }


        setErrors(
          nextErrors,
        );

        return (
          Object.keys(
            nextErrors,
          ).length === 0
        );
      };


    const handleSubmit =
      async (
        event:
          FormEvent<HTMLFormElement>,
      ): Promise<void> => {
        event.preventDefault();

        if (
          isSubmitting ||
          !validateForm()
        ) {
          return;
        }

        const posterFile =
          form.posterFile;

        if (
          !posterFile
        ) {
          return;
        }

        try {
          setIsSubmitting(
            true,
          );


          /*
           * 1. S3 Presigned URL 발급
           */
          const presignData =
            await getPosterPresignUrl(
              posterFile.name,
              posterFile.type,
            );


          /*
           * 2. 실제 S3 업로드
           */
          const uploadResponse =
            await fetch(
              presignData.uploadUrl,
              {
                method: 'PUT',

                headers: {
                  'Content-Type':
                    posterFile.type,
                },

                body:
                  posterFile,
              },
            );


          if (
            !uploadResponse.ok
          ) {
            throw new Error(
              '대회 포스터 이미지 업로드에 실패했습니다.',
            );
          }


          /*
           * 3. 대회 생성
           */
          const request:
            TournamentCreateRequest =
            {
              tournamentTitle:
                form.tournamentTitle.trim(),

              tournamentPoster:
                presignData.imageUrl,

              tournamentEntry:
                Number(
                  form.tournamentEntry,
                ),

              tournamentField:
                form.tournamentField.trim(),

              tournamentTime:
                `${form.date}T${form.startTime}:00`,

              tournamentPrice:
                Number(
                  form.tournamentPrice,
                ),
            };


          await createTournament(
            request,
          );


          const createdMonth =
            form.date.slice(
              0,
              7,
            );


          /*
           * 폼 초기화
           */
          setForm(
            initialForm,
          );

          setErrors(
            {},
          );


          window.alert(
            '대회가 생성되었습니다.',
          );


          /*
           * 부모 페이지에게
           * 생성 완료 전달
           */
          onCreated(
            createdMonth,
          );

        } catch (
          error
        ) {
          console.error(
            '대회 생성 실패:',
            error,
          );

          window.alert(
            getErrorMessage(
              error,
              '대회 생성에 실패했습니다.',
            ),
          );
        } finally {
          setIsSubmitting(
            false,
          );
        }
      };


    const posterUrl =
      form.tournamentPoster;

    const canPreviewPoster =
      Boolean(
        posterUrl,
      );


    return (
      <form
        onSubmit={
          handleSubmit
        }
        noValidate
      >
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">

          <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">

            <div className="mb-5">
              <h2 className="text-lg font-bold text-gray-900">
                대회 기본 정보
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                대회 정보와 포스터를 등록합니다.
              </p>
            </div>


            <div className="space-y-5">

              <FormField
                label="대회 이름"
                required
                error={
                  errors.tournamentTitle
                }
              >
                <div className="relative">

                  <Trophy
                    size={19}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />

                  <input
                    name="tournamentTitle"
                    type="text"
                    value={
                      form.tournamentTitle
                    }
                    onChange={
                      handleInputChange
                    }
                    placeholder="예: 라포이트 시티 메인 이벤트"
                    maxLength={
                      100
                    }
                    className="h-12 w-full rounded-xl border border-gray-300 bg-white pl-10 pr-4 text-sm outline-none transition focus:border-main focus:ring-2 focus:ring-main/10"
                  />

                </div>
              </FormField>


              <FormField
                label="대회 포스터"
                required
                error={
                  errors.posterFile
                }
              >

                {canPreviewPoster ? (

                  <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-gray-100">

                    <div className="h-52 overflow-hidden">

                      <img
                        src={
                          posterUrl
                        }
                        alt="대회 포스터 미리보기"
                        className="h-full w-full object-contain"
                      />

                    </div>


                    <div className="absolute right-3 top-3 flex gap-2">

                      <label className="flex h-10 cursor-pointer items-center gap-2 rounded-lg bg-white px-3 text-sm font-semibold text-gray-700 shadow-md">

                        <Upload
                          size={17}
                        />

                        변경

                        <input
                          type="file"
                          accept="image/jpeg,image/png"
                          onChange={
                            handlePosterChange
                          }
                          className="hidden"
                        />

                      </label>


                      <button
                        type="button"
                        onClick={
                          handleRemovePoster
                        }
                        className="flex h-10 w-10 items-center justify-center rounded-lg bg-white text-red-500 shadow-md"
                        aria-label="포스터 삭제"
                      >
                        <X
                          size={19}
                        />
                      </button>

                    </div>

                  </div>

                ) : (

                  <label
                    onDragEnter={
                      handleDragEnter
                    }
                    onDragOver={
                      handleDragOver
                    }
                    onDragLeave={
                      handleDragLeave
                    }
                    onDrop={
                      handleDrop
                    }
                    className={`flex min-h-[190px] cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-6 text-center transition ${
                      errors.posterFile
                        ? 'border-red-300 bg-red-50/30'
                        : isDragging
                          ? 'border-main bg-main/5'
                          : 'border-gray-300 bg-gray-50 hover:border-gray-400'
                    }`}
                  >

                    <ImagePlus
                      size={34}
                      className="text-gray-400"
                    />

                    <p className="mt-3 text-sm font-semibold text-gray-800">
                      {isDragging
                        ? '여기에 이미지를 놓아주세요.'
                        : '포스터 이미지를 드래그하거나 선택해주세요.'}
                    </p>

                    <p className="mt-1 text-xs text-gray-500">
                      PNG, JPEG · 최대 5MB
                    </p>

                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      onChange={
                        handlePosterChange
                      }
                      className="hidden"
                    />

                  </label>
                )}

              </FormField>


              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

                <FormField
                  label="대회 날짜"
                  required
                  error={
                    errors.date
                  }
                >
                  <input
                    name="date"
                    type="date"
                    value={
                      form.date
                    }
                    onChange={
                      handleInputChange
                    }
                    className="h-12 w-full rounded-xl border border-gray-300 px-4 text-sm outline-none focus:border-main focus:ring-2 focus:ring-main/10"
                  />
                </FormField>


                <FormField
                  label="시작 시각"
                  required
                  error={
                    errors.startTime
                  }
                >
                  <input
                    name="startTime"
                    type="time"
                    value={
                      form.startTime
                    }
                    onChange={
                      handleInputChange
                    }
                    className="h-12 w-full rounded-xl border border-gray-300 px-4 text-sm outline-none focus:border-main focus:ring-2 focus:ring-main/10"
                  />
                </FormField>

              </div>


              <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3">

                <span className="text-xs font-medium text-blue-600">
                  대회 일시
                </span>

                <p className="mt-1 text-sm font-semibold text-blue-900">
                  {
                    formattedSchedule
                  }
                </p>

              </div>


              <FormField
                label="대회 장소"
                required
                error={
                  errors.tournamentField
                }
              >

                <div className="relative">

                  <MapPin
                    size={19}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />

                  <input
                    name="tournamentField"
                    type="text"
                    value={
                      form.tournamentField
                    }
                    onChange={
                      handleInputChange
                    }
                    placeholder="예: 라포이트 인동점"
                    className="h-12 w-full rounded-xl border border-gray-300 pl-10 pr-4 text-sm outline-none focus:border-main focus:ring-2 focus:ring-main/10"
                  />

                </div>

              </FormField>


              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

                <FormField
                  label="MAX 엔트리"
                  required
                  error={
                    errors.tournamentEntry
                  }
                >
                  <input
                    name="tournamentEntry"
                    type="number"
                    min={
                      1
                    }
                    step={
                      1
                    }
                    value={
                      form.tournamentEntry
                    }
                    onChange={
                      handleInputChange
                    }
                    placeholder="예: 63"
                    className="h-12 w-full rounded-xl border border-gray-300 px-4 text-sm outline-none focus:border-main focus:ring-2 focus:ring-main/10"
                  />
                </FormField>


                <FormField
                  label="필요 시드 개수"
                  required
                  error={
                    errors.tournamentPrice
                  }
                >
                  <input
                    name="tournamentPrice"
                    type="number"
                    min={
                      0
                    }
                    step={
                      1
                    }
                    value={
                      form.tournamentPrice
                    }
                    onChange={
                      handleInputChange
                    }
                    placeholder="예: 10"
                    className="h-12 w-full rounded-xl border border-gray-300 px-4 text-sm outline-none focus:border-main focus:ring-2 focus:ring-main/10"
                  />
                </FormField>

              </div>

            </div>

          </section>


          <aside className="space-y-4">

            <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">

              <div className="border-b border-gray-100 px-4 py-3">

                <h2 className="text-sm font-bold text-gray-900">
                  등록 미리보기
                </h2>

              </div>


              <div className="aspect-[4/5] bg-gray-100">

                {canPreviewPoster ? (

                  <img
                    src={
                      posterUrl
                    }
                    alt="포스터 미리보기"
                    className="h-full w-full object-cover"
                  />

                ) : (

                  <div className="flex h-full flex-col items-center justify-center px-6 text-center text-gray-400">

                    <ImagePlus
                      size={38}
                    />

                    <p className="mt-3 text-sm">
                      포스터 이미지를 선택하면 표시됩니다.
                    </p>

                  </div>
                )}

              </div>


              <div className="space-y-3 p-4 text-sm">

                <h3 className="text-lg font-bold">
                  {form.tournamentTitle.trim() ||
                    '대회 이름'}
                </h3>


                <PreviewRow
                  icon={
                    CalendarDays
                  }
                  label="일시"
                  value={
                    formattedSchedule
                  }
                />


                <PreviewRow
                  icon={
                    MapPin
                  }
                  label="장소"
                  value={
                    form.tournamentField.trim() ||
                    '-'
                  }
                />


                <PreviewRow
                  icon={
                    Ticket
                  }
                  label="MAX 엔트리"
                  value={
                    form.tournamentEntry
                      ? `${form.tournamentEntry}개`
                      : '-'
                  }
                />


                <PreviewRow
                  icon={
                    Wallet
                  }
                  label="필요 시드"
                  value={
                    formattedSeedCount
                  }
                />

              </div>

            </section>


            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-lg">

              <button
                type="submit"
                disabled={
                  isSubmitting
                }
                className={`flex h-12 w-full items-center justify-center gap-2 rounded-xl text-sm font-bold ${
                  isSubmitting
                    ? 'cursor-wait bg-gray-300 text-gray-500'
                    : 'bg-main text-white hover:brightness-95'
                }`}
              >

                {isSubmitting ? (

                  <Loader2
                    size={18}
                    className="animate-spin"
                  />

                ) : (

                  <Save
                    size={18}
                  />

                )}


                {isSubmitting
                  ? '대회 생성 중...'
                  : '대회 생성하기'}

              </button>

            </div>

          </aside>

        </div>
      </form>
    );
  };


type FormFieldProps = {
  label: string;

  required?: boolean;

  error?: string;

  children:
    React.ReactNode;
};


const FormField = ({
  label,
  required = false,
  error,
  children,
}: FormFieldProps) => (
  <div>

    <label className="mb-2 block text-sm font-semibold text-gray-800">

      {label}

      {required && (
        <span className="ml-1 text-red-500">
          *
        </span>
      )}

    </label>


    {children}


    {error && (

      <p className="mt-1.5 text-xs text-red-500">
        {error}
      </p>

    )}

  </div>
);


type PreviewRowProps = {
  icon:
    React.ComponentType<{
      size?: number;
      className?: string;
    }>;

  label: string;

  value: string;
};


const PreviewRow = ({
  icon: Icon,
  label,
  value,
}: PreviewRowProps) => (
  <div className="flex items-start gap-2">

    <Icon
      size={17}
      className="mt-0.5 shrink-0 text-gray-400"
    />

    <div className="min-w-0">

      <p className="text-xs text-gray-400">
        {label}
      </p>

      <p className="mt-0.5 break-words font-medium text-gray-700">
        {value}
      </p>

    </div>

  </div>
);


export default TournamentCreateForm;